import { getRuntimeConfig } from '../../config/runtimeConfig';
import { useAuth } from '../../auth';
import { useNotificationHubState } from '../../realtime/NotificationHubProvider';
import { useNotificationPreferences } from '../../stores/useNotificationPreferences';
import { testIds } from '../../testing/testIds';


function EnvironmentBadge() {
  const colorMap: Record<string, string> = {
    Development: 'bg-green-100 text-green-800 border-green-300',
    Staging: 'bg-amber-100 text-amber-800 border-amber-300',
    Production: 'bg-red-100 text-red-800 border-red-300',
  };

  const colors = colorMap[getRuntimeConfig().envName] || colorMap.Development;

  // Don't show badge in production (it's the "normal" state)
  if (getRuntimeConfig().envName === 'Production') return null;

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded border ${colors}`}>
      {getRuntimeConfig().envName}
    </span>
  );
}

function HubStatusDot() {
  const state = useNotificationHubState();
  const color =
    state === 'Connected'
      ? 'bg-green-500'
      : state === 'Connecting' || state === 'Reconnecting'
        ? 'bg-amber-400'
        : 'bg-gray-300';
  const label =
    state === 'Connected'
      ? 'Live notifications connected'
      : state === 'Connecting'
        ? 'Connecting to notifications'
        : state === 'Reconnecting'
          ? 'Reconnecting notifications'
          : 'Notifications disconnected';

  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${color}`}
      title={label}
      aria-label={label}
      role="status"
    />
  );
}

interface HeaderProps {
  navigationPinned: boolean;
  navigationOpen: boolean;
  onToggleNavigation: () => void;
  navigationMenuRef: RefObject<HTMLButtonElement>;
}

export function Header({ navigationPinned, navigationOpen, onToggleNavigation, navigationMenuRef }: HeaderProps) {
  const { user, logout } = useAuth();
  const silenced = useNotificationPreferences((s) => s.silenced);
  const toggleSilenced = useNotificationPreferences((s) => s.toggleSilenced);

  return (
    <header className="min-h-14 bg-white border-b border-gray-200 flex items-center justify-between gap-3 px-3 sm:px-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          ref={navigationMenuRef}
          onClick={onToggleNavigation}
          data-testid={testIds.shell.navigationMenuButton}
          aria-label={navigationOpen ? 'Close navigation' : 'Open navigation'}
          aria-controls="main-navigation"
          aria-expanded={navigationPinned ? undefined : navigationOpen}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5" aria-hidden>
            {navigationOpen ? (
              <path strokeLinecap="round" d="m6 6 12 12M18 6 6 18" />
            ) : (
              <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
            )}
          </svg>
        </button>
        <EnvironmentBadge />
        <HubStatusDot />
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        <button
          type="button"
          onClick={() => toggleSilenced()}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          title={silenced ? 'Unmute notifications' : 'Mute notifications'}
          aria-pressed={silenced}
        >
          <span className="hidden sm:inline">{silenced ? 'Notifications muted' : 'Mute notifications'}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5 sm:hidden" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
          </svg>
        </button>
        <span className="hidden sm:inline text-sm text-gray-600">
          {user?.profile?.name || user?.profile?.preferred_username || 'User'}
        </span>
        <button
          onClick={() => logout()}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          Sign out
        </button>
      </div>
    </header>
  );
}
import type { RefObject } from 'react';
